import React from 'react';
import { RegisterModuleStateProps } from '@/components/modules/register/register-dynamic/types';
import { useFlutterEvents } from '@/components/utils/hooks/events/useFlutterEvents';
import { GLOBAL_FLUTTER_CHANNELS, REGISTER_EVENTS } from '@/components/utils/hooks/events/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import MarketingEvents from '@/utils/marketing/MarketingEvents';
import { getShortLiveToken } from '@/modules/casino/store/actions/login';
import { logSegmentRegistrationComplete } from '@/analytics/segment';
import { logRegistrationEvent, logWithProfileData } from '@/analytics';

export function useRegisterEvents(state: RegisterModuleStateProps, marketingOfferValue?: any) {
  const events = useFlutterEvents();
  const dispatch = useAppDispatch();

  const { signedUp, signUpLoading } = useAppSelector((state) => state.register);
  const { client_player_id } = useAppSelector<any>((state) => state.profile);
  const { shortLiveToken } = useAppSelector((state) => state.login);
  const [actionsTaken, setActionsTaken] = React.useState<string[]>([]);
  const [marketingOfferLogged, setMarketingOfferLogged] = React.useState<boolean>(false);

  React.useEffect(() => {
    events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
      event: REGISTER_EVENTS.FORM_READY,
    });

    return () => {
      // This event was removed based on the discussion with Paz and Iulian
      // https://teams.microsoft.com/l/message/19:0a4f9cb72d4b4245aaff0bf0e0ff63b6@thread.v2/1753171771438?context=%7B%22contextType%22%3A%22chat%22%7D
      // events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
      //   event: REGISTER_EVENTS.FORM_CLOSE,
      // });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (signUpLoading) {
      events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
        event: REGISTER_EVENTS.FORM_SUBMIT,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUpLoading]);

  React.useEffect(() => {
    // if (state.errors.global) {
    //   events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
    //     event: REGISTER_EVENTS.SERVER_OR_NETWORK_ERROR,
    //     payload: {
    //       message: state.errors.global,
    //     },
    //   });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.errors.global]);

  React.useEffect(() => {
    if (state.OCRModalOpen) {
      events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
        event: REGISTER_EVENTS.FORM_UPLOAD_DOCUMENT,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.OCRModalOpen]);

  React.useEffect(() => {
    if (signedUp) {
      if (window.config.appEnv === 'poker') {
        dispatch(getShortLiveToken());
      }
      MarketingEvents.signUpCompleted({ playerId: client_player_id });

      // Use the utility function to wait for profile data before logging
      logWithProfileData(logSegmentRegistrationComplete, [state.fields]);
      logWithProfileData(logRegistrationEvent, [
        'registration_completed',
        actionsTaken,
        state.currentStep.toString(),
        state.errors,
      ]);

      // Google Tag Manager
      try {
        // @ts-ignore
        if (window.dataLayer) {
          // @ts-ignore
          window.dataLayer.push({
            registration: 'complete',
            event: 'registration-complete',
          });
        }
      } catch (err) {}
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client_player_id]);

  React.useEffect(() => {
    if (signedUp && client_player_id && shortLiveToken) {
      if (window.config.logPokerEvents === '1') {
        console.log('Poker Event Data:', {
          event: REGISTER_EVENTS.AUTO_LOGIN,
          payload: {
            token: shortLiveToken,
          },
        });
      }
      events.send(GLOBAL_FLUTTER_CHANNELS.POKER_APP_EVENT, {
        event: REGISTER_EVENTS.AUTO_LOGIN,
        payload: {
          token: shortLiveToken,
        },
      });
    }
  }, [signedUp, client_player_id, shortLiveToken, events]);

  // Effect for page visibility and unload event logging
  React.useEffect(() => {
    let isLogging = false;
    let isPageClosing = false;

    const handleLog = (eventName: string) => {
      if (isLogging || actionsTaken.length === 0) return;
      try {
        isLogging = true;
        logRegistrationEvent(eventName, actionsTaken, state.currentStep.toString(), state.errors);
      } finally {
        isLogging = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && !isPageClosing) {
        handleLog('registration_page_hidden');
      }
    };

    const handleUnload = () => {
      isPageClosing = true;
      handleLog('registration_page_closed');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [actionsTaken, state.currentStep, state.errors]);

  // Effect to track actions taken based on state trigger
  React.useEffect(() => {
    // skip if it is certain internal triggers that shouldn't be logged
    if (
      state.trigger === 'useEffect-resendTimer' ||
      state.trigger.includes('Focus') ||
      state.trigger.includes('Blur')
    ) {
      return;
    }

    setActionsTaken((prevState) => {
      return [...prevState, state.trigger];
    });
  }, [state.trigger]);

  // Effect to log marketing offer received event (only once)
  React.useEffect(() => {
    if (marketingOfferValue && !marketingOfferLogged) {
      logRegistrationEvent(
        'registration_received_marketing_offer',
        actionsTaken,
        state.currentStep.toString(),
        state.errors,
      );
      setMarketingOfferLogged(true);
    }
  }, [marketingOfferValue, marketingOfferLogged, actionsTaken, state.currentStep, state.errors]);

  return {
    actionsTaken,
    setActionsTaken,
    marketingOfferLogged,
    setMarketingOfferLogged,
  };
}